import React from "react";
import { toast } from "@/shared/utils/toast";
import EnrollmentBatchProgressToastPanel from "@/features/campaigns/components/EnrollmentBatchProgressToastPanel";
import { useEnrollmentBatchToastStore } from "@/features/campaigns/stores/enrollmentBatchToastStore";

export const ENROLLMENT_BATCH_TOAST_ID = "sw-enrollment-batch";
export const ACCOUNT_BATCH_STORAGE_KEY = "enrollmentBatch:account";

/**
 * Account research jobs reuse the enrollment batch toast. Their batchJobId is
 * prefixed so the tracker polls account-job endpoints instead of the enrollment
 * batch endpoint.
 */
export const ACCOUNT_RESEARCH_JOB_PREFIX = "account-job:";
export const ACCOUNT_RESEARCH_JOBS_PREFIX = "account-jobs:";

export const isAccountResearchJobBatchId = (batchJobId) =>
  typeof batchJobId === "string" &&
  (batchJobId.startsWith(ACCOUNT_RESEARCH_JOB_PREFIX) ||
    batchJobId.startsWith(ACCOUNT_RESEARCH_JOBS_PREFIX));

export const extractAccountResearchJobIds = (batchJobId) => {
  if (batchJobId?.startsWith(ACCOUNT_RESEARCH_JOBS_PREFIX)) {
    return batchJobId
      .slice(ACCOUNT_RESEARCH_JOBS_PREFIX.length)
      .split(",")
      .filter(Boolean);
  }
  if (batchJobId?.startsWith(ACCOUNT_RESEARCH_JOB_PREFIX)) {
    return [batchJobId.slice(ACCOUNT_RESEARCH_JOB_PREFIX.length)];
  }
  return [];
};

/** Open (and persist) the progress toast for a single account research job. */
export function startAccountResearchJobToast(jobId) {
  return startAccountResearchJobsToast([jobId]);
}

/** Open (and persist) progress for one or more account research jobs. */
export function startAccountResearchJobsToast(jobIds = []) {
  const validJobIds = [...new Set(jobIds.filter(Boolean))];
  if (!validJobIds.length) return false;
  const payload = {
    batchJobId:
      validJobIds.length === 1
        ? `${ACCOUNT_RESEARCH_JOB_PREFIX}${validJobIds[0]}`
        : `${ACCOUNT_RESEARCH_JOBS_PREFIX}${validJobIds.join(",")}`,
    mode: "account",
    total: validJobIds.length,
    count: validJobIds.length,
    createdAt: new Date().toISOString(),
  };
  persistEnrollmentBatch(payload);
  openEnrollmentBatchToast(payload);
  return true;
}

export function normalizeEnrollmentProgress(payload = {}) {
  const mode = payload.mode || "campaign";
  const count = Number(payload.count ?? 0);
  const total =
    mode === "account"
      ? Number(payload.total ?? count)
      : Number(count || payload.total || 0);

  return {
    batchJobId: payload.batchJobId ? String(payload.batchJobId) : "",
    campaignId: payload.campaignId ? String(payload.campaignId) : "",
    mode,
    total,
    count: count || total,
    contentDone: Number(payload.contentDone ?? 0),
    profileDone: Number(payload.profileDone ?? payload.researchDone ?? 0),
    accountDone: Number(payload.accountDone ?? 0),
    failed: Number(payload.failed ?? 0),
    skipped: Array.isArray(payload.skipped) ? payload.skipped : [],
    accountSummary: payload.accountSummary ?? null,
    isComplete: Boolean(payload.isComplete),
    isDismissed: Boolean(payload.isDismissed),
  };
}

export function resolveBatchTotal(data, mode = "campaign") {
  const batchMode = mode || data?.mode || "campaign";
  const count = Number(data?.count ?? 0);
  if (batchMode === "account") {
    return Number(data?.total ?? count);
  }
  return count || Number(data?.total ?? 0);
}

export function startEnrollmentBatchFromResponse(
  data,
  { campaignId, mode } = {},
) {
  const batchMode = mode ?? data?.mode ?? "campaign";
  const total = resolveBatchTotal(data, batchMode);

  if (!data?.batchJobId) return false;

  if (total <= 0) {
    dismissEnrollmentBatchToast();
    return false;
  }

  const payload = {
    batchJobId: data.batchJobId,
    campaignId: campaignId ? String(campaignId) : "",
    mode: batchMode,
    total,
    count: Number(data?.count ?? 0) || total,
    skipped: data.skipped || [],
    accountSummary: data.accountSummary ?? null,
    createdAt: new Date().toISOString(),
  };

  persistEnrollmentBatch(payload);
  openEnrollmentBatchToast(payload);
  window.dispatchEvent(
    new CustomEvent("enrollmentBatchStarted", { detail: payload }),
  );
  return true;
}

export function persistEnrollmentBatch(payload = {}) {
  if (typeof sessionStorage === "undefined") return;
  const normalized = normalizeEnrollmentProgress(payload);
  if (!normalized.batchJobId) return;

  const storagePayload = {
    ...normalized,
    createdAt: payload.createdAt || new Date().toISOString(),
  };

  if (normalized.mode === "account") {
    sessionStorage.setItem(
      ACCOUNT_BATCH_STORAGE_KEY,
      JSON.stringify(storagePayload),
    );
    return;
  }

  if (normalized.campaignId) {
    sessionStorage.setItem(
      `enrollmentBatch:${normalized.campaignId}`,
      JSON.stringify(storagePayload),
    );
  }
}

export function openEnrollmentBatchToast(payload = {}) {
  const normalized = normalizeEnrollmentProgress(payload);
  const shouldOpen =
    normalized.mode === "account"
      ? normalized.total > 0 && Boolean(normalized.batchJobId)
      : Number(normalized.count ?? 0) > 0;

  if (!shouldOpen) {
    return;
  }

  useEnrollmentBatchToastStore.getState().reset();
  useEnrollmentBatchToastStore.getState().setPatch(normalized);

  if (!toast.isActive(ENROLLMENT_BATCH_TOAST_ID)) {
    toast(
      ({ closeToast }) =>
        React.createElement(EnrollmentBatchProgressToastPanel, {
          onDismiss: () => {
            useEnrollmentBatchToastStore
              .getState()
              .setPatch({ isDismissed: true });
            closeToast();
          },
        }),
      {
        toastId: ENROLLMENT_BATCH_TOAST_ID,
        position: "bottom-right",
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        draggable: false,
        hideProgressBar: true,
        icon: false,
        className:
          "!bg-transparent !shadow-none !p-0 !m-0 !border-0 !min-h-0 !w-max !max-w-none",
        bodyClassName: "!p-0 !m-0",
      },
    );
  }
}

export function pickEnrollmentProgressFields(payload = {}) {
  if (!payload || typeof payload !== "object") return {};

  const patch = {};
  if (payload.mode) patch.mode = payload.mode;
  if (payload.total !== undefined) patch.total = Number(payload.total);
  if (payload.contentDone !== undefined) {
    patch.contentDone = Number(payload.contentDone);
  }
  if (payload.profileDone !== undefined) {
    patch.profileDone = Number(payload.profileDone);
  } else if (payload.researchDone !== undefined) {
    patch.profileDone = Number(payload.researchDone);
  }
  if (payload.accountDone !== undefined) {
    patch.accountDone = Number(payload.accountDone);
  }
  if (payload.failed !== undefined) patch.failed = Number(payload.failed);
  return patch;
}

export function patchEnrollmentBatchToast(partial = {}) {
  useEnrollmentBatchToastStore.getState().setPatch(partial);
}

export function dismissEnrollmentBatchToast({ reset = true } = {}) {
  if (toast.isActive(ENROLLMENT_BATCH_TOAST_ID)) {
    toast.dismiss(ENROLLMENT_BATCH_TOAST_ID);
  }

  if (reset) {
    useEnrollmentBatchToastStore.getState().reset();
  } else {
    useEnrollmentBatchToastStore.getState().setPatch({ isDismissed: true });
  }
}
